import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, CheckCircle2, Users, Target, BookOpen, GraduationCap } from 'lucide-react';

export const Home = () => {
  return (
    <div className="space-y-32 pb-32">
      {/* Hero Section */}
      <section className="container mx-auto px-4 md:px-6 pt-12 md:pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-1.5 rounded-full text-primary text-xs font-bold uppercase tracking-widest border border-blue-100">
              <GraduationCap size={14} />
              Educação de Qualidade
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-primary leading-[1.1] tracking-tight">
              Construindo <span className="text-secondary">Cidadãos</span> Conscientes.
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
              A Escola Estadual 8 de Maio tem como missão contribuir para a formação de cidadãos críticos, capazes de atuar como agentes de transformação social.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link
                to="/atividades"
                className="bg-primary text-white px-8 py-4 rounded-xl font-bold hover:bg-secondary transition-all flex items-center justify-center gap-2 group shadow-lg shadow-blue-900/10"
              >
                Nossas Atividades
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/sobre-nos"
                className="bg-white border border-slate-200 text-primary px-8 py-4 rounded-xl font-bold hover:bg-slate-50 transition-all text-center"
              >
                Nossa História
              </Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-square bg-slate-100 rounded-3xl overflow-hidden shadow-2xl border-8 border-white">
              <img
                src="https://i.postimg.cc/Z5H3wkCQ/Whats-App-Image-2026-03-12-at-18-54-12.jpg"
                alt="Fachada da Escola Estadual 8 de Maio"
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quem Somos Section Preview */}
      <section className="bg-white py-32 border-y border-slate-100">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold text-primary tracking-tight">
              Quem Somos
            </h2>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Desenvolvemos o senso crítico, moral e cooperativo, incentivando nossos estudantes na construção de uma sociedade igualitária com ênfase na colaboração mútua e solidária.
            </p>
            <Link
              to="/sobre-nos"
              className="inline-flex items-center gap-2 text-secondary font-bold border-b-2 border-secondary pb-1 hover:text-primary hover:border-primary transition-all"
            >
              Saiba mais sobre nossa visão
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Objetivos Section */}
      <section className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-end gap-8 mb-16">
          <div className="space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-primary tracking-tight">Nossos Objetivos</h2>
            <p className="text-slate-500 max-w-md">Trabalhamos diariamente para alcançar a excelência no processo educacional.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: 'Desempenho Acadêmico', desc: 'Melhorar o desempenho dos estudantes nas avaliações externas e da aprendizagem.', icon: <TrendingUp /> },
            { title: 'Trabalho em Equipe', desc: 'Desenvolver e reforçar o trabalho em equipe e a melhor utilização das tecnologias.', icon: <Users /> },
            { title: 'Envolvimento Familiar', desc: 'Aumentar o envolvimento da família e da comunidade escolar no dia a dia.', icon: <Heart /> },
          ].map((item, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -10 }}
              className="bg-white p-10 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group"
            >
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center text-primary mb-8 group-hover:bg-primary group-hover:text-white transition-colors">
                {item.icon || <BookOpen />}
              </div>
              <h3 className="text-2xl font-bold text-primary mb-4">{item.title}</h3>
              <p className="text-slate-500 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      <section className="bg-slate-50 py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-primary tracking-tight">Momentos da Nossa Escola</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">Registros de atividades, eventos e a participação da nossa comunidade escolar.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                url: 'https://i.postimg.cc/YCzNTGy1/Whats-App-Image-2003-12-at-18-54-14.jpg', 
                title: 'Programa Agrinho',
                desc: 'Participação ativa dos nossos alunos no programa.'
              },
              { 
                url: 'https://i.postimg.cc/vTnfhcVH/Whats-App-Image-2026-03-12-at-18-54-14.jpg', 
                title: 'Reconhecimento',
                desc: 'Bandeira da escola no palco do Programa Agrinho.'
              },
              { 
                url: 'https://i.postimg.cc/JhdXWb3j/Whats-App-Image-2026-03-12-at-18-54-13.jpg', 
                title: 'Natal Solidário',
                desc: 'Entrega de presentes para a comunidade.'
              },
              { 
                url: 'https://i.postimg.cc/Z5H3wkCQ/Whats-App-Image-2026-03-12-at-18-54-12.jpg', 
                title: 'Nossa Sede',
                desc: 'Fachada da Escola Estadual 8 de Maio.'
              }
            ].map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="group relative aspect-[3/4] overflow-hidden rounded-2xl shadow-lg"
              >
                <img 
                  src={img.url} 
                  alt={img.title} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6 text-white">
                  <h4 className="font-bold text-lg">{img.title}</h4>
                  <p className="text-sm text-slate-200">{img.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const TrendingUp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
);

const Heart = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path></svg>
);
